import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { hashPin, getPinHash, setPinHash, clearPin, isPinEnabled } from "@/lib/pinLock";
import { Lock, Delete } from "lucide-react";

const KEY_FAILS = "pk2_pin_fails";
const KEY_LOCKED_UNTIL = "pk2_pin_locked_until";
const KEY_UNLOCKED = "pk2_pin_unlocked";  // sessionStorage flag — cleared on tab close
const MAX_FAILS = 5;
const LOCK_SECONDS = 30;

/**
 * Full-screen PIN lock overlay. Renders nothing if PIN is disabled or already
 * unlocked this session. Intercepts all interaction until the correct PIN is
 * entered. Offers "Olvidé mi PIN" → logs the user out, clears the PIN, and
 * returns them to login.
 *
 * The PIN is asked ONCE per browser session. Once unlocked, the user can
 * navigate freely until they close the tab/browser.
 */
export default function PinLockScreen() {
  const { logout } = useAuth();
  const [enabled, setEnabled] = useState(false);
  // Initialize unlocked from sessionStorage so route changes don't re-lock
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(KEY_UNLOCKED) === "1"; } catch { return false; }
  });
  const [digits, setDigits] = useState([]);
  const [error, setError] = useState("");
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const expectedLen = useRef(4);

  useEffect(() => {
    const hash = getPinHash();
    if (!hash) { setEnabled(false); return; }
    setEnabled(true);
    // How long is the PIN? hash length is always 64 but the original PIN length
    // was also stored. Fall back to 4 if missing.
    const len = parseInt(localStorage.getItem("pk2_pin_len") || "4", 10);
    expectedLen.current = Math.max(4, Math.min(6, len));

    const lu = parseInt(localStorage.getItem(KEY_LOCKED_UNTIL) || "0", 10);
    if (lu > Date.now()) setLockedUntil(lu);
  }, []);

  useEffect(() => {
    if (!lockedUntil) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [lockedUntil]);

  useEffect(() => {
    const verify = async () => {
      if (digits.length !== expectedLen.current) return;
      const stored = getPinHash();
      const incoming = await hashPin(digits.join(""));
      if (incoming === stored) {
        localStorage.setItem(KEY_FAILS, "0");
        localStorage.removeItem(KEY_LOCKED_UNTIL);
        try { sessionStorage.setItem(KEY_UNLOCKED, "1"); } catch {}
        setUnlocked(true);
      } else {
        const fails = parseInt(localStorage.getItem(KEY_FAILS) || "0", 10) + 1;
        localStorage.setItem(KEY_FAILS, String(fails));
        setError(`PIN incorrecto (${fails}/${MAX_FAILS})`);
        setDigits([]);
        if (fails >= MAX_FAILS) {
          const lu = Date.now() + LOCK_SECONDS * 1000;
          localStorage.setItem(KEY_LOCKED_UNTIL, String(lu));
          localStorage.setItem(KEY_FAILS, "0");
          setLockedUntil(lu);
        }
      }
    };
    verify();
  }, [digits]);

  if (!enabled || unlocked) return null;

  const locked = lockedUntil > now;
  const secondsLeft = Math.max(0, Math.ceil((lockedUntil - now) / 1000));

  const tap = (n) => {
    if (locked) return;
    setError("");
    if (digits.length < expectedLen.current) setDigits([...digits, n]);
  };
  const back = () => {
    if (locked) return;
    setError("");
    setDigits(digits.slice(0, -1));
  };

  const forgot = async () => {
    clearPin();
    try { sessionStorage.removeItem(KEY_UNLOCKED); } catch {}
    await logout();
    window.location.href = "/acceder";
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{ background: "rgba(5,5,5,0.97)", backdropFilter: "blur(20px)" }}
      data-testid="pin-lock-screen"
    >
      <Lock size={32} strokeWidth={1.3} color="var(--brand-primary)" className="mb-3" />
      <p className="overline mb-2" style={{ color: "var(--brand-primary)" }}>Bloqueada</p>
      <h2 className="font-serif text-3xl mb-6" style={{ color: "var(--text-primary)" }}>
        Ingresa tu PIN
      </h2>

      {/* PIN dots */}
      <div className="flex gap-3 mb-8" data-testid="pin-dots">
        {Array.from({ length: expectedLen.current }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 16, height: 16, borderRadius: "50%",
              background: i < digits.length ? "var(--brand-primary)" : "transparent",
              border: "2px solid var(--brand-primary)",
              transition: "background 0.15s",
            }}
          />
        ))}
      </div>

      {locked ? (
        <p className="text-sm mb-4" style={{ color: "#ef6d6d" }} data-testid="pin-locked-msg">
          Demasiados intentos. Espera {secondsLeft}s.
        </p>
      ) : error && (
        <p className="text-sm mb-4" style={{ color: "#ef6d6d" }} data-testid="pin-error">{error}</p>
      )}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3" style={{ width: 260 }} data-testid="pin-numpad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button
            key={n}
            onClick={() => tap(n)}
            disabled={locked}
            className="font-serif text-2xl"
            style={{
              width: 76, height: 76,
              background: "var(--bg-surface)",
              border: "1px solid var(--border-accent)",
              color: "var(--text-primary)",
              transition: "all 0.15s",
              opacity: locked ? 0.3 : 1,
            }}
            data-testid={`pin-digit-${n}`}
          >
            {n}
          </button>
        ))}
        <div />
        <button
          onClick={() => tap(0)}
          disabled={locked}
          className="font-serif text-2xl"
          style={{
            width: 76, height: 76,
            background: "var(--bg-surface)",
            border: "1px solid var(--border-accent)",
            color: "var(--text-primary)",
            opacity: locked ? 0.3 : 1,
          }}
          data-testid="pin-digit-0"
        >
          0
        </button>
        <button
          onClick={back}
          disabled={locked}
          className="flex items-center justify-center"
          style={{
            width: 76, height: 76,
            background: "transparent",
            border: "1px solid var(--border-subtle)",
            color: "var(--text-secondary)",
            opacity: locked ? 0.3 : 1,
          }}
          data-testid="pin-backspace"
        >
          <Delete size={20} strokeWidth={1.3} />
        </button>
      </div>

      <button
        onClick={forgot}
        className="text-xs mt-8 underline"
        style={{ color: "var(--text-muted)" }}
        data-testid="pin-forgot-btn"
      >
        Olvidé mi PIN
      </button>
    </div>
  );
}

export { isPinEnabled, setPinHash, clearPin, hashPin };
